import classNames from 'classnames';
import { type CSSProperties, useEffect } from 'react';
import { createPortal } from 'react-dom';

import { loadingImage, loadingImageDark } from '@revelio/assets';
import { useLoadingGif } from '@revelio/core';
import { Callout, Spinner, Text } from '@revelio/ui';

import { Chart } from '../CustomDash/components/Chart/ChartRoot/Chart';
import { getEntityTypeFromPrimaryFilter } from '../CustomDash/components/EditableGrid/getEntityTypeFromPrimaryFilter';
import {
  usePageConfig,
  useTabContentConfig,
} from '../CustomDash/components/PageConfigProvider/PageConfigProvider';
import {
  GLOBAL_AI_INLINE_CHART_LOADING_HEIGHT,
  GLOBAL_AI_INLINE_CHART_METRIC_LOADING_HEIGHT,
} from '../CustomDash/constants/panelWidths';
import {
  TRIAL_COMPANY_RESTRICTION_TITLE,
  TrialCompanyRestrictionMessage,
} from '../CustomDash/filters/hooks/TrialCompanyRestrictionMessage';
import { GaChartPreviewMetaProvider } from './ga-chart-preview-meta-context';
import { toCustomDashAssistantEntityType } from './global-ai-assistant-api';
import { GlobalAIAssistantChartFilterScope } from './global-ai-assistant-chart-filter-scope';
import styles from './global-ai-assistant-chart-portal.module.css';
import { useGlobalAIAssistantOptional } from './global-ai-assistant-context';
import type { GlobalAiInlineChartSpec } from './global-ai-assistant-transcript';
import { useGlobalAIAssistantFeatureEnabled } from './use-global-ai-assistant-feature-enabled';

const PREVIEW_LOADER_GIF_WIDTH = 85;

export function GlobalAIAssistantChartPortal({
  mount,
  spec,
}: {
  mount: HTMLElement;
  spec: GlobalAiInlineChartSpec;
}) {
  const enabled = useGlobalAIAssistantFeatureEnabled();
  const gaCtx = useGlobalAIAssistantOptional();
  const { config: pageConfig } = usePageConfig();
  const { primaryFilter } = useTabContentConfig();

  // Skill and activity pages are not assistant entities yet (follow-up PR),
  // so they read as "no entity" here, the same as a page without a primary.
  const entityFromPrimary = getEntityTypeFromPrimaryFilter(primaryFilter);
  const entityNow = toCustomDashAssistantEntityType(
    entityFromPrimary === null ||
      entityFromPrimary === 'skill' ||
      entityFromPrimary === 'activity'
      ? undefined
      : entityFromPrimary
  );
  const runtime = gaCtx?.chartPreviewRuntimeByMessageId[spec.messageId];
  const chartType = spec.item.plot?.chartType;
  const secondaryDimension = spec.item.plot?.secondaryDimension;
  const dimensionSource = spec.item.plot?.dimensionSource;
  const hasBreakdownShape =
    secondaryDimension !== undefined || dimensionSource !== undefined;
  const isTextChart = chartType === 'text';
  const usesCompactMetricLayout = chartType === 'bar' && !hasBreakdownShape;
  const previewLoadingMinHeight = (() => {
    if (isTextChart) {
      return 'min-content';
    }
    if (usesCompactMetricLayout) {
      return GLOBAL_AI_INLINE_CHART_METRIC_LOADING_HEIGHT;
    }
    return GLOBAL_AI_INLINE_CHART_LOADING_HEIGHT;
  })();
  const chartBodyMinHeight = (() => {
    if (isTextChart) {
      return 'min-content';
    }
    if (runtime?.phase === 'success') {
      return 'min-content';
    }
    return previewLoadingMinHeight;
  })();
  const loadingGif = useLoadingGif();
  // The default-brand gif has a dark-theme variant; the light one relies on a
  // multiply blend that goes invisible against the dark themed drawer.
  const darkGif = loadingGif === loadingImage ? loadingImageDark : undefined;
  const showPreviewLoadingOverlay =
    runtime?.phase !== 'success' && runtime?.phase !== 'error';

  useEffect(() => {
    if (!gaCtx) {
      return;
    }
    if (!entityNow || entityNow !== spec.entityType) {
      const blocked = `blocked: entity mismatch; expected:${spec.entityType}; current:${entityNow ?? 'none'}`;
      if (
        runtime?.phase === 'error' &&
        runtime.providerDiagnostics === blocked
      ) {
        return;
      }
      gaCtx.mergeChartPreviewRuntime(spec.messageId, {
        phase: 'error',
        providerDiagnostics: blocked,
      });
      gaCtx.addChartPreviewTimelineEvent(
        spec.messageId,
        `blocked entity mismatch (expected ${spec.entityType}, current ${entityNow ?? 'none'})`
      );
    }
  }, [gaCtx, entityNow, runtime, spec.entityType, spec.messageId]);

  if (!enabled) {
    return null;
  }

  if (!entityNow || entityNow !== spec.entityType) {
    const mismatchMessage = (
      <Text fontSize="x-sm" className={styles.mismatchMessage}>
        Chart preview will appear once this dashboard matches the answer type (
        {spec.entityType}).
      </Text>
    );
    return createPortal(
      isTextChart ? (
        mismatchMessage
      ) : (
        <div
          className={classNames(styles.mismatchCard, styles.mismatchPadding)}
        >
          {mismatchMessage}
        </div>
      ),
      mount
    );
  }

  const previewErrorMessage =
    runtime?.phase === 'error'
      ? (runtime.filterApplyError ?? runtime.providerDiagnostics)
      : undefined;
  // A plan limit, not a fault — same wording and upgrade link as the toast the
  // non-assistant surfaces show, so the refusal reads identically either way.
  const blockedCompanies =
    runtime?.phase === 'error'
      ? runtime.filterApplyBlockedCompanies
      : undefined;
  const previewErrorTitle = (() => {
    if (blockedCompanies) return TRIAL_COMPANY_RESTRICTION_TITLE;
    if (runtime?.filterApplyError) return 'Could not apply chart filters';
    return 'Could not render this summary';
  })();

  const chartPreviewBody = (
    <div
      className={styles.chartPreviewBody}
      style={{ '--chart-body-min-height': chartBodyMinHeight } as CSSProperties}
    >
      {previewErrorMessage || blockedCompanies ? (
        <Callout
          variant={blockedCompanies ? 'warning' : 'error'}
          className={classNames(
            styles.previewAlert,
            isTextChart ? styles.roundCorners : styles.squareCorners
          )}
          data-testid="global-ai-chart-preview-notice"
          title={previewErrorTitle}
          description={
            <div className={styles.previewError}>
              {blockedCompanies ? (
                <TrialCompanyRestrictionMessage
                  blockedNames={blockedCompanies}
                />
              ) : (
                previewErrorMessage
              )}
            </div>
          }
        />
      ) : (
        <>
          <GlobalAIAssistantChartFilterScope
            messageId={spec.messageId}
            filters={spec.filters}
            revelioPageId={pageConfig.revelioPageId}
            primaryFilterId={primaryFilter?.id}
            loadingMinHeight={previewLoadingMinHeight}
            compactLoading={isTextChart}
          >
            <GaChartPreviewMetaProvider
              messageId={spec.messageId}
              itemId={spec.item.id}
            >
              <Chart
                item={spec.item}
                primaryFilter={primaryFilter}
                editMode={false}
              />
            </GaChartPreviewMetaProvider>
          </GlobalAIAssistantChartFilterScope>
          {showPreviewLoadingOverlay && isTextChart ? (
            <div className={styles.summaryLoading}>
              <Spinner size="sm" />
              <Text fontSize="x-sm" className={styles.summaryLoadingLabel}>
                Writing summary…
              </Text>
            </div>
          ) : null}
        </>
      )}
      {showPreviewLoadingOverlay && !isTextChart ? (
        <div
          data-testid={`global-ai-assistant-chart-preview-loading-${spec.messageId}`}
          className={styles.loadingOverlay}
        >
          {loadingGif ? (
            <>
              <img
                src={loadingGif}
                alt="Loading chart"
                className={`${styles.previewLoaderGif}${
                  darkGif ? ` ${styles.previewLoaderGifHasDark}` : ''
                }`}
                style={{ width: `${PREVIEW_LOADER_GIF_WIDTH}px` }}
              />
              {darkGif ? (
                <img
                  src={darkGif}
                  aria-hidden
                  alt=""
                  className={styles.previewLoaderGifDark}
                  style={{ width: `${PREVIEW_LOADER_GIF_WIDTH}px` }}
                />
              ) : null}
            </>
          ) : (
            <Spinner size="md" />
          )}
        </div>
      ) : null}
    </div>
  );

  if (isTextChart) {
    return createPortal(
      <div className={styles.chartPreview}>{chartPreviewBody}</div>,
      mount
    );
  }

  return createPortal(
    <div className={classNames(styles.chartCard, styles.chartPreview)}>
      {chartPreviewBody}
      {spec.chartSubtitle ? (
        <Text fontSize="x-sm" className={styles.chartSubtitle} tone="muted">
          {spec.chartSubtitle}
        </Text>
      ) : null}
    </div>,
    mount
  );
}
